import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Lightbulb, ArrowRight, Database, Wrench, FolderPlus, CheckCircle2 } from 'lucide-react';
import { DbChallenge, DbChallengeSolution, SOLUTION_TYPES } from '@/hooks/useChallengeLab';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface SolutionDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  solution: DbChallengeSolution | null;
  challenge?: DbChallenge | null;
  onConvertToProject: (solution: DbChallengeSolution) => void;
}

export function SolutionDetailDialog({ open, onOpenChange, solution, challenge, onConvertToProject }: SolutionDetailDialogProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!solution) return null;

  const typeInfo = SOLUTION_TYPES.find(t => t.id === solution.solution_type) || { icon: '📦', name: solution.solution_type || 'General', description: '' };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">{typeInfo.icon}</span>
              {solution.name}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-2">
              <Badge variant="outline">{typeInfo.name}</Badge>
              <span className="text-xs">
                {format(new Date(solution.created_at), "d 'de' MMMM, yyyy", { locale: es })}
              </span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {solution.description && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-secondary" />
                  Descripción de la Solución
                </h4>
                <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">{solution.description}</p>
              </div>
            )}

            {solution.data_used && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <Database className="w-4 h-4 text-primary" />
                  Datos Utilizados
                </h4>
                <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">{solution.data_used}</p>
              </div>
            )}

            {solution.suggested_tools && solution.suggested_tools.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-primary" />
                  Herramientas
                </h4>
                <div className="flex flex-wrap gap-2">
                  {solution.suggested_tools.map((tool) => <Badge key={tool} variant="secondary">{tool}</Badge>)}
                </div>
              </div>
            )}

            {solution.converted_to_project ? (
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <div className="flex items-center gap-2 text-emerald-600">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">Convertido a Proyecto</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">Este prospecto ya fue convertido a un proyecto activo.</p>
              </div>
            ) : (
              <Button onClick={() => setConfirmOpen(true)} className="w-full gradient-primary" size="lg">
                <FolderPlus className="w-4 h-4 mr-2" />
                Convertir a Proyecto
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Crear proyecto desde esta solución?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>Se creará un nuevo proyecto con la siguiente información:</p>
                <div className="bg-muted/50 rounded-lg p-3 space-y-1 text-sm">
                  <p><strong>Nombre:</strong> {solution.name}</p>
                  <p><strong>Tipo:</strong> {typeInfo.icon} {typeInfo.name}</p>
                  {challenge?.description && (
                    <p><strong>Problema:</strong> {challenge.description.slice(0, 100)}{challenge.description.length > 100 ? '…' : ''}</p>
                  )}
                  {challenge?.kpi_name && (
                    <p><strong>KPI:</strong> {challenge.kpi_name} ({challenge.kpi_base_value} → {challenge.kpi_expected_value})</p>
                  )}
                  {challenge?.target_audience && (
                    <p><strong>Audiencia:</strong> {challenge.target_audience}</p>
                  )}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              setConfirmOpen(false);
              onConvertToProject(solution);
            }}>
              Crear Proyecto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
